<template>
  <div class="hubspot-success">
    <div class="success-card">
      <!-- Loading State -->
      <div v-if="loading" class="success-card-content">
        <div class="loading-container">
          <div class="loading-spinner"></div>
        </div>
        <h1 class="success-title">
          {{ $t('developer-tab.hubspot-credentials.success.loading-title') }}
        </h1>
        <p class="success-description">
          {{
            $t('developer-tab.hubspot-credentials.success.loading-description')
          }}
        </p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="success-card-content">
        <div class="error-icon-container">
          <div class="error-icon">
            <i class="fa fa-times"></i>
          </div>
        </div>

        <h1 class="success-title error-title">
          {{ $t('developer-tab.hubspot-credentials.success.error-title') }}
        </h1>

        <p class="success-description">
          {{
            $t('developer-tab.hubspot-credentials.success.error-description')
          }}
        </p>

        <div class="success-actions">
          <button class="btn btn-primary" @click="completeHubspotConnection">
            {{ $t('developer-tab.hubspot-credentials.success.retry-button') }}
          </button>
          <button class="btn btn-secondary" @click="goToHubspot">
            {{ $t('developer-tab.hubspot-credentials.success.back-button') }}
          </button>
        </div>
      </div>

      <!-- Success State -->
      <div v-else class="success-card-content">
        <div class="success-icon-container">
          <div class="success-icon">
            <i class="fa fa-check"></i>
          </div>
        </div>

        <h1 class="success-title">
          {{ $t('developer-tab.hubspot-credentials.success.title') }}
        </h1>

        <p class="success-description">
          {{ $t('developer-tab.hubspot-credentials.success.description') }}
        </p>

        <div class="success-features">
          <div class="success-feature-item">
            <i class="fa fa-check-circle"></i>
            <span>{{
              $t('developer-tab.hubspot-credentials.success.features.synced')
            }}</span>
          </div>
          <div class="success-feature-item">
            <i class="fa fa-check-circle"></i>
            <span>{{
              $t('developer-tab.hubspot-credentials.success.features.ready')
            }}</span>
          </div>
        </div>

        <div class="success-actions">
          <button class="btn btn-primary" @click="goToHubspot">
            {{ $t('developer-tab.hubspot-credentials.success.back-button') }}
          </button>
          <router-link :to="{ name: 'dashboard' }" class="btn btn-secondary">
            {{
              $t('developer-tab.hubspot-credentials.success.dashboard-button')
            }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import hubspotService from '../../../../services/hubspot.service'

export default {
  name: 'HubspotSuccess',
  data() {
    return {
      hubspotUrl: 'https://app.hubspot.com',
      loading: true,
      error: false
    }
  },
  methods: {
    goToHubspot() {
      window.open(this.hubspotUrl, '_blank')
    },
    async completeHubspotConnection() {
      const code = this.$route.query.code
      this.loading = true
      this.error = false
      try {
        await hubspotService.completeHubspotConnection(code)
        this.toast(this.$t('developer-tab.hubspot-credentials.success.toast'))
      } catch (error) {
        console.log(error)
        this.error = true
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.completeHubspotConnection()
  }
}
</script>

<style scoped lang="scss">
.hubspot-success {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.success-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
  overflow: hidden;
}

.success-card-content {
  padding: 48px 40px;
  text-align: center;
}

.success-icon-container {
  margin-bottom: 24px;
}

.success-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border-radius: 50%;
  animation: scaleIn 0.5s ease-out;

  i {
    font-size: 36px;
    color: #ffffff;
  }
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading-container {
  margin-bottom: 24px;
}

.loading-spinner {
  display: inline-block;
  width: 60px;
  height: 60px;
  border: 4px solid #e2e8f0;
  border-top-color: #ff7a59;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-icon-container {
  margin-bottom: 24px;
}

.error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  border-radius: 50%;
  animation: scaleIn 0.5s ease-out;

  i {
    font-size: 36px;
    color: #ffffff;
  }
}

.error-title {
  color: #e53e3e;
}

.success-title {
  font-size: 28px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
}

.success-description {
  font-size: 16px;
  color: #718096;
  margin-bottom: 32px;
  line-height: 1.6;
}

.success-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

.success-feature-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  color: #48bb78;

  i {
    font-size: 16px;
  }
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .btn {
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s ease;
  }

  .btn-primary {
    background-color: #ff7a59;
    color: #ffffff;
    border: none;

    &:hover {
      background-color: #ff5c35;
    }
  }

  .btn-secondary {
    background-color: transparent;
    color: #4a5568;
    border: 1px solid #e2e8f0;

    &:hover {
      background-color: #f7fafc;
    }
  }
}
</style>
